import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../../utils/api";
import { toast } from "../../utils/toast";
import { useTeamFilter } from "../../utils/useTeamFilter";
import { FileText, ArrowLeft, RefreshCw, User, Edit3, RefreshCcw, Trash2, Users, Search, ChevronLeft, ChevronRight } from "lucide-react";
import "./filemonitor.css";
import "../devices/devices.css";

const LOGS_PAGE_SIZE = 25;

const FileMonitor = () => {
    const navigate = useNavigate();
    const [agents, setAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [fileLogs, setFileLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [logsLoading, setLogsLoading] = useState(false);
    const [counts, setCounts] = useState({ rename: 0, rewrite: 0, delete: 0 });
    const { departments, getTeamName } = useTeamFilter();
    const [selectedTeam, setSelectedTeam] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [logsPage, setLogsPage] = useState(1);

    // Fetch all agents with file logs
    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        try {
            setLoading(true);
            const response = await apiGet("/api/file-monitor/agents");
            if (response.ok) {
                const data = await response.json();
                setAgents(data.agents || []);
            } else {
                toast.error("Failed to fetch agents");
            }
        } catch (err) {
            console.error("Failed to fetch agents:", err);
            toast.error("Failed to fetch agents");
        } finally {
            setLoading(false);
        }
    };

    // Fetch file logs for selected agent
    const fetchAgentLogs = async (agentId) => {
        try {
            setLogsLoading(true);
            const response = await apiGet(`/api/file-monitor/logs/${agentId}`);
            if (response.ok) {
                const data = await response.json();
                setFileLogs(data.logs || []);
                setCounts(data.counts || { rename: 0, rewrite: 0, delete: 0 });
            } else {
                toast.error("Failed to fetch file logs");
            }
        } catch (err) {
            console.error("Failed to fetch file logs:", err);
            toast.error("Failed to fetch file logs");
        } finally {
            setLogsLoading(false);
        }
    };

    const handleAgentClick = (agent) => {
        setSelectedAgent(agent);
        setLogsPage(1);
        fetchAgentLogs(agent.agentId);
    };

    const handleBack = () => {
        setSelectedAgent(null);
        setFileLogs([]);
        setCounts({ rename: 0, rewrite: 0, delete: 0 });
        setLogsPage(1);
    };

    const getRowClass = (type) => {
        switch (type) {
            case "Rename":
                return "log-rename";
            case "Rewrite":
                return "log-rewrite";
            case "Delete":
                return "log-delete";
            default:
                return "";
        }
    };

    const getEventBadgeClass = (type) => {
        switch (type) {
            case "Rename":
                return "event-badge event-rename";
            case "Rewrite":
                return "event-badge event-rewrite";
            case "Delete":
                return "event-badge event-delete";
            default:
                return "event-badge";
        }
    };

    const getEventIcon = (type) => {
        switch (type) {
            case "Rename":
                return <Edit3 size={12} />;
            case "Rewrite":
                return <RefreshCcw size={12} />;
            case "Delete":
                return <Trash2 size={12} />;
            default:
                return null;
        }
    };

    // Agent List View
    if (!selectedAgent) {
        const filteredAgents = agents
            .filter((agent) => selectedTeam === "all" || getTeamName(agent.agentId) === selectedTeam)
            .filter((agent) => {
                const q = searchTerm.trim().toLowerCase();
                if (!q) return true;
                return (agent.agentId || "").toLowerCase().includes(q) || (agent.hostname || "").toLowerCase().includes(q);
            });

        return (
            <div className="filemonitor-wrapper">
                <div className="filemonitor-header">
                    <p className="filemonitor-subtitle">
                        Select an agent to view their file activity logs
                    </p>
                    <button className="gc-icon-btn" data-tip="Refresh" onClick={fetchAgents} disabled={loading}>
                        <RefreshCw size={14} />
                    </button>
                </div>

                <div className="team-filter-row">
                    <div className="name-search-wrapper">
                        <Search size={16} className="team-filter-icon" />
                        <input
                            type="text"
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {departments.length > 0 && (
                        <div className="team-select-wrapper">
                            <Users size={16} className="team-filter-icon" />
                            <select value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
                                <option value="all">All Teams</option>
                                {departments.map((dept) => (
                                    <option key={dept._id} value={dept.name}>{dept.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="loading-state">Loading agents...</div>
                ) : filteredAgents.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <p>No agents with file logs found</p>
                    </div>
                ) : (
                    <div className="agents-grid">
                        {filteredAgents.map((agent, idx) => (
                            <div
                                key={idx}
                                className="agent-card"
                                onClick={() => handleAgentClick(agent)}
                            >
                                <div className="agent-icon">
                                    <User size={24} />
                                </div>
                                <div className="agent-info">
                                    <h3>{agent.agentId}</h3>
                                    <p className="agent-hostname">{agent.hostname || "Unknown"}</p>
                                    <div className="agent-counts">
                                        <span className="count-rename">{agent.renameCount || 0} Rename</span>
                                        <span className="count-rewrite">{agent.rewriteCount || 0} Rewrite</span>
                                        <span className="count-delete">{agent.deleteCount || 0} Delete</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Agent Detail View with Logs
    const logsTotalPages = Math.max(1, Math.ceil(fileLogs.length / LOGS_PAGE_SIZE));
    const pagedLogs = fileLogs.slice((logsPage - 1) * LOGS_PAGE_SIZE, logsPage * LOGS_PAGE_SIZE);

    return (
        <div className="filemonitor-wrapper">
            <div className="filemonitor-header">
                <button className="gc-icon-btn" onClick={handleBack} disabled={logsLoading} data-tip="Back to Agents" aria-label="Back to Agents">
                    <ArrowLeft size={18} />
                </button>
                <h1 className="filemonitor-title">
                    <FileText size={24} />
                    {selectedAgent.agentId}
                </h1>
                <button className="gc-icon-btn" data-tip="Refresh" onClick={() => fetchAgentLogs(selectedAgent.agentId)} disabled={logsLoading}>
                    <RefreshCw size={14} />
                </button>
            </div>

            {/* Stats Cards */}
            <div className="filemonitor-stats-grid">
                <div className="filemonitor-stat-card">
                    <div className="stat-icon rename">
                        <Edit3 size={20} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{counts.rename}</span>
                        <span className="stat-label">Rename</span>
                    </div>
                </div>
                <div className="filemonitor-stat-card">
                    <div className="stat-icon rewrite">
                        <RefreshCcw size={20} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{counts.rewrite}</span>
                        <span className="stat-label">Rewrite</span>
                    </div>
                </div>
                <div className="filemonitor-stat-card">
                    <div className="stat-icon delete">
                        <Trash2 size={20} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{counts.delete}</span>
                        <span className="stat-label">Delete</span>
                    </div>
                </div>
            </div>

            {/* Logs Table */}
            <div className="filemonitor-content">
                {logsLoading ? (
                    <div className="loading-state">Loading logs...</div>
                ) : fileLogs.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <p>No file logs found for this agent</p>
                    </div>
                ) : (
                    <table className="logs-table">
                        <thead>
                            <tr>
                                <th>Timestamp</th>
                                <th>Filename</th>
                                <th>File Modified Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedLogs.map((log, i) => (
                                <tr key={i} className={`log-row ${getRowClass(log.eventType)}`}>
                                    <td className="timestamp-cell">{new Date(log.timestamp).toLocaleString()}</td>
                                    <td>{log.details?.filename || "Unknown"}</td>
                                    <td>
                                        <span className={getEventBadgeClass(log.eventType)}>
                                            {getEventIcon(log.eventType)}
                                            {log.eventType}
                                        </span>
                                    </td>
                                    <td className="description-cell">{log.description || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {fileLogs.length > 0 && (
                    <div className="devices-pagination">
                        <span className="devices-pagination-count">
                            {(logsPage - 1) * LOGS_PAGE_SIZE + 1}–{Math.min(logsPage * LOGS_PAGE_SIZE, fileLogs.length)} of {fileLogs.length} event{fileLogs.length !== 1 ? "s" : ""}
                        </span>
                        <div className="devices-pagination-controls">
                            <button
                                className="gc-icon-btn"
                                data-tip="Previous page"
                                onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                                disabled={logsPage <= 1}
                            >
                                <ChevronLeft size={15} />
                            </button>
                            <span className="devices-pagination-page">{logsPage} / {logsTotalPages}</span>
                            <button
                                className="gc-icon-btn"
                                data-tip="Next page"
                                onClick={() => setLogsPage((p) => Math.min(logsTotalPages, p + 1))}
                                disabled={logsPage >= logsTotalPages}
                            >
                                <ChevronRight size={15} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <p className="logs-footer">
                Showing {fileLogs.length} file events
            </p>
        </div>
    );
};

export default FileMonitor;
